import { propsUtil } from "@asphalt-react/helper"
import { getOffset, shouldAdjustArrow, ARROW_SIZE } from "./helper.js"

const { resolvePropCollision } = propsUtil

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useClick,
  useInteractions,
  useHover,
  useFocus,
  arrow,
} from "@floating-ui/react"

/**
 * Custom hook to manage Popover behavior and interactions.
 *
 * @param {object} props - Configuration object for usePopover hook.
 * @param {boolean} props.open - Indicates whether the Popover is open.
 * @param {string} [props.placement] - Placement of the Popover relative to the target.
 * @param {boolean} [props.outOfFlow] - If true, removes the Popover from its parent's flow.
 * @param {boolean} [props.flip] - Enables flipping the Popover position if space is constrained.
 * @param {boolean} [props.shift] - Enables shifting the Popover to avoid viewport overflow.
 * @param {string} [props.size] - Size of the Popover ("s", "m", "l").
 * @param {boolean} [props.bezel] - Adds padding around the Popover.
 * @param {boolean} [props.stretch] - Enables the Popover to stretch to fill its container.
 * @param {string[]} [props.focusOrder] - Order in which focus cycles ("reference", "floating", "content").
 * @param {boolean} [props.returnFocus] - Determines if focus should return to the reference element.
 * @param {number|object} [props.initialFocus] - Element to initially focus when the Popover opens.
 * @param {boolean} [props.focusTrap] - Enables focus trapping within the Popover.
 * @param {boolean} [props.noOffset] - Removes spacing between the target and Popover.
 * @param {boolean} [props.lowOffset] - Adds low spacing between the target and Popover.
 * @param {boolean} [props.mediumOffset] - Adds medium spacing between the target and Popover.
 * @param {boolean} [props.highOffset] - Adds high spacing between the target and Popover.
 * @param {Function} [props.onOpenChange] - Callback triggered when the open state changes.
 * @param {boolean} [props.isHover=false] - Enables hover interactions for the Popover.
 * @param {boolean} [props.isClick=true] - Enables click interactions for the Popover.
 * @param {object|null} [props.arrowRef] - Ref for the arrow element.
 * @param {object} [props.rest] - Additional props are spread to the underlying floating-ui hook.
 *
 * @returns {object} return - Prop getter functions for the Popover components.
 * @returns {Function} return.getTargetProps - Returns props for the target element.
 * @returns {Function} return.getPopoverProps - Returns props for the Popover element.
 * @returns {Function} return.getFocusProps - Returns props for the FocusManager component.
 */
export const usePopover = ({
  open,
  placement,
  outOfFlow,
  flip: flipPosition,
  shift: shiftContent,
  size,
  bezel,
  stretch,
  focusOrder,
  returnFocus,
  initialFocus,
  focusTrap,
  noOffset,
  lowOffset,
  mediumOffset,
  highOffset,
  onOpenChange,
  isHover = false,
  isClick = true,
  arrowRef,
  ...props
}) => {
  const { collision, value: offsetProp } = resolvePropCollision(
    {
      noOffset,
      lowOffset,
      mediumOffset,
      highOffset,
    },
    "mediumOffset"
  )

  if (collision) {
    console.warn(
      `usePopover: Multiple offsets detected, falling back to "${offsetProp}"`
    )
  }

  const arrowMiddleware =
    arrowRef !== null
      ? arrow({
          element: arrowRef,
        })
      : null

  const middleware = [
    offset(({ rects }) => {
      const alignmentAxis = shouldAdjustArrow(rects?.reference, placement)
        ? { alignmentAxis: -ARROW_SIZE }
        : {}
      return {
        mainAxis: getOffset(offsetProp),
        ...alignmentAxis,
      }
    }),
    ...(flipPosition ? [flip()] : []),
    ...(shiftContent ? [shift()] : []),
    arrowMiddleware,
  ]

  const { x, y, refs, strategy, context } = useFloating({
    ...props,
    open,
    placement,
    strategy: outOfFlow ? "fixed" : "absolute",
    onOpenChange,
    middleware,
    whileElementsMounted: autoUpdate,
  })

  const click = isClick ? useClick(context) : null
  const dismiss = useDismiss(context)
  const hover = isHover
    ? useHover(context, {
        delay: {
          open: 300,
          close: 100,
        },
      })
    : null

  // Opens the floating element while the reference element has focus on keyboard navigation.
  const focus = isHover ? useFocus(context) : null

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    hover,
    focus,
  ])

  return {
    /**
     * Returns props for the target element.
     *
     * @returns {Object} - Props for the target element.
     */
    getTargetProps: () => {
      return {
        ref: refs.setReference,
        ...getReferenceProps(),
      }
    },
    /**
     * Returns props for the Popover element.
     *
     * @returns {Object} - Props for the Popover element.
     */
    getPopoverProps: () => {
      return {
        open,
        size,
        bezel,
        ref: refs.setFloating,
        stretch,
        anchorStyles: {
          position: strategy,
          top: y ?? 0,
          left: x ?? 0,
        },
        ...getFloatingProps(),
      }
    },
    /**
     * Returns props for the FocusManager component.
     *
     * @returns {Object} - Props for the FocusManager component.
     */
    getFocusProps: () => {
      return {
        initialFocus,
        focusOrder,
        focusTrap,
        popoverContext: context,
        returnFocus,
      }
    },
  }
}
